<template>
    <section class="section wizard-section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title has-text-centered mb-5">{{ translate("create_event_wizard.title") }}</h1>
                </div>
            </div>
            <div class="columns is-centered">
                <div class="column is-8-desktop">
                    <div class="box p-5">
                        <template v-if="props.draftLimitError !== ''">
                            <h2 class="title is-4 wizard-title">{{ translate("wizard.draft_limit_title") }}</h2>
                            <p class="wizard-subtitle">{{ translate(props.draftLimitError) }}</p>
                            <div class="mt-5">
                                <a class="button is-primary" :href="manageEventsUrl">{{ translate("wizard.go_to_manage_events") }}</a>
                            </div>
                        </template>
                        <template v-else>
                            <WizardStepIndicator :current-step="wizard.currentStep" :total-steps="5" />
                            <p class="wizard-step-label">
                                {{ translate("wizard.step_of_total", false, [wizard.currentStep, 5]) }}
                            </p>
                            <h2 class="title is-4 wizard-title">{{ translate(stepHeading.title) }}</h2>
                            <p class="wizard-subtitle">{{ translate(stepHeading.subtitle) }}</p>

                            <Transition name="wizard-step" mode="out-in">
                                <Step1Basics v-if="wizard.currentStep === 1" @submit="onStep1Submit" />
                                <Step2Location
                                    v-else-if="wizard.currentStep === 2"
                                    @submit="onStep2Submit"
                                    @back="wizard.currentStep = 1"
                                />
                                <Step3Registration
                                    v-else-if="wizard.currentStep === 3"
                                    @submit="onStep3Submit"
                                    @back="wizard.currentStep = 2"
                                />
                                <Step4Images
                                    v-else-if="wizard.currentStep === 4"
                                    @continue="onStep4Continue"
                                    @back="wizard.currentStep = 3"
                                />
                                <Step5Categories
                                    v-else-if="wizard.currentStep === 5"
                                    @submit="onFinalSubmit"
                                    @back="wizard.currentStep = 4"
                                />
                            </Transition>

                            <div class="has-text-right mt-3">
                                <a class="wizard-skip-link" @click="!skipping && onSkipOrSaveLater()">
                                    <InlineSpinner v-if="skipping" />
                                    <span v-else>{{ skipLabel }}</span>
                                </a>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import WizardStepIndicator from "./WizardStepIndicator.vue";
    import InlineSpinner from "../../Common/InlineSpinner.vue";
    import Step1Basics from "./steps/Step1Basics.vue";
    import Step2Location from "./steps/Step2Location.vue";
    import Step3Registration from "./steps/Step3Registration.vue";
    import Step4Images from "./steps/Step4Images.vue";
    import Step5Categories, { type StagedCategoriesRecipe, type CategoriesSetupOutcome } from "./steps/Step5Categories.vue";
    import { useWizardStore } from "./wizardStore";
    import { useEditEventStore } from "../editEventStore";
    import eventsApi, { WizardCategoriesSetupMethod, WizardCategoriesSetupRequest, WizardRegistrationStageDto } from "../../Api/events";
    import HttpError from "../../Api/Utils/HttpError";
    import urls from "../../Urls/urls";
    import { useAnalytics } from "../../Common/Analytics/useAnalytics";
    import { AnalyticsEvents, StandardProps } from "../../Common/Analytics/analyticsEvents";
    import { handleError } from "../../Common/errorHandler";
    import { warningToast } from "../../Common/toasts";
    import { translate } from "../../Common/translations";
    import { useScrollResetOnMount } from "../../Common/useScrollResetOnMount";

    useScrollResetOnMount();

    const props = defineProps<{
        federationId: string;
        federationCountry: string;
        organizerPhone: string | null;
        draftLimitError: string;
        isFreshOrganizer: boolean;
    }>();

    const wizard = useWizardStore();
    const editEventStore = useEditEventStore();
    const { trackEvent } = useAnalytics();
    const analyticsProps = computed(() => ({
        [StandardProps.ORGANIZER_TYPE]: props.isFreshOrganizer ? "first_time" : "returning",
    }));
    const skipping = ref(false);
    const manageEventsUrl = urls.eventAdmin().manageEventsPage();

    wizard.federationId = props.federationId;
    wizard.organizerPhone = props.organizerPhone;
    // Prefill the event location country from the federation; a restored draft (loaded in
    // onMounted) still takes precedence.
    wizard.country = props.federationCountry || null;

    const STEP_HEADINGS = {
        1: { title: "wizard.step1.title", subtitle: "wizard.step1.subtitle" },
        2: { title: "wizard.step2.title", subtitle: "wizard.step2.subtitle" },
        3: { title: "wizard.step3.title", subtitle: "wizard.step3.subtitle" },
        4: { title: "wizard.step4.title", subtitle: "wizard.step4.subtitle" },
        5: { title: "wizard.step5.title", subtitle: "wizard.step5.subtitle" },
    } as const;

    const stepHeading = computed(() => STEP_HEADINGS[wizard.currentStep]);

    const skipLabel = computed(() => {
        return wizard.currentStep === 1 ? translate("wizard.skip_wizard") : translate("wizard.cancel_setup");
    });

    // Records how far the organizer reached, even if no event is ever created (PRD #894 §34.1).
    // Fire-and-forget — must never disrupt the wizard flow.
    function reportWizardProgress(furthestStep: number) {
        eventsApi.recordWizardProgress(wizard.federationId, furthestStep).catch(() => {});
    }

    function onStep1Submit() {
        trackEvent(AnalyticsEvents.Organizer.WizardStep1Completed, analyticsProps.value);
        reportWizardProgress(2);
        wizard.currentStep = 2;
    }

    function onStep2Submit() {
        trackEvent(AnalyticsEvents.Organizer.WizardStep2Completed, analyticsProps.value);
        reportWizardProgress(3);
        wizard.currentStep = 3;
    }

    function onStep3Submit() {
        trackEvent(AnalyticsEvents.Organizer.WizardStep3Completed, analyticsProps.value);
        reportWizardProgress(4);
        wizard.currentStep = 4;
    }

    function onStep4Continue() {
        trackEvent(AnalyticsEvents.Organizer.WizardStep4Completed, analyticsProps.value);
        reportWizardProgress(5);
        wizard.currentStep = 5;
    }

    function onFinalSubmit(recipe: StagedCategoriesRecipe | null, categoriesSetupMethod: CategoriesSetupOutcome) {
        if (wizard.saving) {
            return;
        }
        wizard.saving = true;
        wizard.validationErrors = {};

        const step5AnalyticsProps = { ...analyticsProps.value, categoriesSetupMethod };
        if (categoriesSetupMethod === "template" || categoriesSetupMethod === "copyFromPreviousEvent") {
            trackEvent(AnalyticsEvents.Organizer.WizardStep5Completed, step5AnalyticsProps);
        } else {
            trackEvent(AnalyticsEvents.Organizer.WizardStep5Skipped, step5AnalyticsProps);
        }

        const categoriesSetup: WizardCategoriesSetupRequest | null =
            recipe === null
                ? null
                : {
                      method: categoriesSetupMethod as WizardCategoriesSetupMethod,
                      ...recipe,
                  };

        return eventsApi
            .createWizardEvent(wizard.federationId, {
                fullName: wizard.fullName,
                timezone: wizard.timezone,
                startDate: wizard.startDate || null,
                endDate: wizard.endDate || null,
                eventTypes: wizard.eventTypes,
                country: wizard.country,
                place: wizard.place || null,
                contactEmail: wizard.contactEmail || null,
                contactPhone: wizard.contactPhone || null,
                registrationStages: wizard.registrationStages.map((stage, i) =>
                    i === 0 && wizard.registrationOpenDate ? { ...stage, openDate: wizard.registrationOpenDate } : stage,
                ) as WizardRegistrationStageDto[],
                categoriesSetup: categoriesSetup,
            })
            .then(({ eventId }) => {
                trackEvent(AnalyticsEvents.Organizer.FirstDraftCreated, analyticsProps.value);

                editEventStore.eventId = eventId;
                editEventStore.organizerPhone = wizard.organizerPhone;

                const uploads: Promise<unknown>[] = [];
                if (wizard.logoFile) {
                    uploads.push(eventsApi.uploadImage(eventId, "thumb", wizard.logoFile));
                }
                if (wizard.backgroundFile) {
                    uploads.push(eventsApi.uploadImage(eventId, "bkg", wizard.backgroundFile));
                }

                return Promise.allSettled(uploads).then(results => {
                    const anyFailed = results.some(r => r.status === "rejected");
                    if (anyFailed) {
                        warningToast(translate("wizard.images_upload_failed"), 6000);
                    }
                    return editEventStore.fetchFullEvent();
                });
            })
            .then(() => {
                wizard.clearDraft();
                window.location.replace(urls.eventAdmin().editEventPageToOnboardingTour(editEventStore.eventId));
            })
            .catch((err: HttpError) => {
                if (err.validationErrors) {
                    wizard.validationErrors = Object.fromEntries(err.validationErrors.map(e => [e.field, e.message]));
                } else {
                    handleError(err);
                }
                if (categoriesSetup !== null) {
                    trackEvent(AnalyticsEvents.Organizer.WizardStep5Completed, { ...step5AnalyticsProps, outcome: "failed" });
                }
                wizard.saving = false;
            });
    }

    function onSkipOrSaveLater() {
        if (wizard.currentStep === 1) {
            if (!confirm(translate("wizard.confirm_skip"))) {
                return;
            }
            trackEvent(AnalyticsEvents.Organizer.WizardSkipped, analyticsProps.value);
            wizard.clearDraft();
            skipping.value = true;

            eventsApi
                .createDraftEvent({ timezone: wizard.timezone, federationId: wizard.federationId })
                .then(({ eventId }) => {
                    trackEvent(AnalyticsEvents.Organizer.FirstDraftCreated, analyticsProps.value);
                    window.location.replace(urls.eventAdmin().editEventPageToOnboardingTour(eventId));
                })
                .catch(err => {
                    handleError(err);
                    skipping.value = false;
                });
        } else {
            if (!confirm(translate("wizard.confirm_cancel"))) {
                return;
            }
            trackEvent(AnalyticsEvents.Organizer.WizardAbandoned, analyticsProps.value);
            wizard.clearDraft();
            window.location.replace(urls.eventAdmin().manageEventsPage());
        }
    }

    onMounted(() => {
        wizard.loadDraft();
        wizard.$subscribe((_, state) => {
            if (!state.saving) {
                wizard.persistDraft();
            }
        });
        trackEvent(AnalyticsEvents.Organizer.WizardStarted, analyticsProps.value);
        reportWizardProgress(1);
    });
</script>
